import type { ButtonHTMLAttributes, ReactNode } from 'react';
import Link from 'next/link';
import { clsx } from 'clsx';
import { ArrowDownIcon, ArrowRightIcon } from '../icons';
import styles from './marketing.module.css';

type ButtonVariant = 'primary' | 'outline' | 'text' | 'unstyled';
type ButtonArrow = 'forward' | 'down';

interface ButtonBaseProps {
  children: ReactNode;
  /**
   * `primary` is the amber pill. `outline` is transparent with an amber edge on
   * light surfaces and a soft-white edge on midnight (inherited from a `dark`
   * section, or forced with `onDark`). `text` is the underlined inline button.
   *
   * `unstyled` carries no marketing class at all: `className` is the whole
   * skin and any glyph rides in `children`. It exists for a page that owns a
   * reviewed skin of its own (Reputation's cream pill with its stroked arrow)
   * and wants only the semantics and the action, not for restyling a button
   * on a redesigned page.
   */
  variant?: ButtonVariant;
  /** Force the midnight treatment outside a dark section, e.g. over media. */
  onDark?: boolean;
  /**
   * The design system's arrow policy: a forward arrow only on buttons that move
   * the reader toward the scanner or off the page; a down arrow on the mobile
   * scan shortcut. Most buttons carry none.
   */
  arrow?: ButtonArrow;
  className?: string;
}

type ButtonAsButton = ButtonBaseProps &
  Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'className' | 'children'> & { href?: undefined };

type ButtonAsLink = ButtonBaseProps & {
  href: string;
  onClick?: (event: React.MouseEvent<HTMLAnchorElement>) => void;
  'aria-label'?: string;
};

export type ButtonProps = ButtonAsButton | ButtonAsLink;

function ArrowGlyph({ arrow }: { arrow: ButtonArrow }): React.ReactElement {
  return arrow === 'down' ? (
    <ArrowDownIcon className={styles.arrowDown} />
  ) : (
    <ArrowRightIcon variant="hairline" className={styles.arrowForward} />
  );
}

/**
 * The marketing button: pill, 16/24 DM Sans 600, 48px tall, 200ms colour
 * feedback, the target itself stays still. Renders an anchor when `href` is
 * given (a Next link for internal routes), a `<button>` otherwise.
 *
 * States are the native ones. A `<button>` takes every button attribute, so
 * `disabled`, `aria-busy` and `aria-haspopup` pass straight through and the
 * disabled treatment is the shared one on every variant; a pending request
 * is `disabled` plus `aria-busy` plus the owner's label, because the library
 * does not model requests. A link takes no `disabled`: the design system has
 * no disabled-link imitation.
 */
export function Button(props: ButtonProps): React.ReactElement {
  const { children, variant = 'primary', onDark = false, arrow, className } = props;
  const pill = variant === 'primary' || variant === 'outline';
  const classes = clsx(
    pill && styles.btn,
    variant === 'outline' && styles.btnOutline,
    variant === 'outline' && onDark && styles.btnOnDark,
    variant === 'text' && styles.textButton,
    className
  );
  const content = (
    <>
      {children}
      {pill && arrow ? <ArrowGlyph arrow={arrow} /> : null}
    </>
  );

  if ('href' in props && props.href !== undefined) {
    const { href, onClick } = props;
    const ariaLabel = props['aria-label'];
    const internal = href.startsWith('/') || href.startsWith('#');
    if (internal && !href.startsWith('#')) {
      return (
        <Link href={href} className={classes} onClick={onClick} aria-label={ariaLabel}>
          {content}
        </Link>
      );
    }
    return (
      <a href={href} className={classes} onClick={onClick} aria-label={ariaLabel}>
        {content}
      </a>
    );
  }

  const {
    variant: _variant,
    onDark: _onDark,
    arrow: _arrow,
    className: _className,
    children: _children,
    href: _href,
    ...rest
  } = props;
  return (
    <button type="button" className={classes} {...rest}>
      {content}
    </button>
  );
}
